import type { ReactNode } from "react";
import { useMemo } from "react";
import { StyleSheet, View } from "react-native";

import { type Colors, useColors } from "./theme";
import { AppSidebar } from "./app-sidebar";
import { BottomTabBar } from "./bottom-tab-bar";
import { useResponsive } from "./use-responsive";

interface Props {
  /** The screen body. On wide it fills the pane beside the sidebar. */
  children: ReactNode;
  /** User chip primary line (web sidebar only). */
  userLabel: string;
  /** User chip secondary line, e.g. "CDL · California" (web sidebar only). */
  metaLabel?: string;
}

// Chrome wrapper for the paid product surface (Home, Settings). Drives
// design_handoff_2 Rule B: native/narrow gets the bottom tab bar under
// the content; web/wide swaps it for a persistent 236px left sidebar.
// Screens render the same body in both — only the navigation chrome and
// its position change.
export function TabScaffold({
  children,
  userLabel,
  metaLabel,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isWide } = useResponsive();

  if (isWide) {
    return (
      <View style={styles.wideRow}>
        <AppSidebar userLabel={userLabel} metaLabel={metaLabel} />
        <View style={styles.wideMain}>{children}</View>
      </View>
    );
  }

  return (
    <View style={styles.narrow}>
      <View style={styles.narrowBody}>{children}</View>
      <BottomTabBar />
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    wideRow: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: colors.background,
    },
    wideMain: { flex: 1 },
    narrow: { flex: 1, backgroundColor: colors.background },
    narrowBody: { flex: 1 },
  });
}
